"use client";

import React from "react";
import { Building2, Store, ShoppingBag, MapPin, Compass, Globe as Globe2, ArrowRightLeft, BriefcaseBusiness, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
interface AudienceItem {
  id: number;
  title: string;
  badge: string;
  description: string;
  benefit: string;
  iconName: string;
  to: string;
}
const audienceList: AudienceItem[] = [{
  id: 1,
  title: "UK SMEs",
  badge: "Scale & Efficiency",
  description: "Growing mid-sized enterprises that need high-calibre search strategy to scale organic visibility efficiently as the business grows.",
  benefit: "Agile execution focused on sustainable, scalable search growth",
  iconName: "Building2",
  to: "/ServicesPage"
}, {
  id: 2,
  title: "Small businesses",
  badge: "Focused ROI",
  description: "Ambitious independent companies wanting clear, sustainable organic rankings that translate directly into enquiries.",
  benefit: "Practical roadmaps with transparent priorities",
  iconName: "Store",
  to: "/ServicesPage"
}, {
  id: 3,
  title: "B2B businesses",
  badge: "High-Intent Leads",
  description: "Service providers and software firms aiming to capture decision-makers searching for specialized commercial solutions.",
  benefit: "Search pipelines built around qualified deal value",
  iconName: "BriefcaseBusiness",
  to: "/B2BSeoLeadGenerationPage"
}, {
  id: 4,
  title: "Ecommerce stores",
  badge: "Revenue Growth",
  description: "Online retailers seeking clean category architectures, technical health, and higher commercial term visibility.",
  benefit: "Category page optimization and crawler efficiency",
  iconName: "ShoppingBag",
  to: "/EcommerceSeoPage"
}, {
  id: 5,
  title: "Local businesses",
  badge: "Local Dominance",
  description: "Regional operators who rely on local map packs, geo-targeted visibility, and customers in defined territories.",
  benefit: "Optimised local landing pages and Google profiles",
  iconName: "MapPin",
  to: "/LocalSEOPage"
}, {
  id: 6,
  title: "National brands",
  badge: "Country-Wide Reach",
  description: "Organisations competing across the United Kingdom for competitive, high-volume industry terms.",
  benefit: "Deep technical audits and authoritative content depth",
  iconName: "Compass",
  to: "/NationalSeoPage"
}, {
  id: 7,
  title: "International markets",
  badge: "Cross-Border SEO",
  description: "Brands expanding into international regions that require robust hreflang, ccTLD, and localized market strategy.",
  benefit: "Multi-regional search structures without index bloat",
  iconName: "Globe2",
  to: "/InternationalSeoPage"
}, {
  id: 8,
  title: "Leaving an agency",
  badge: "Direct Partnership",
  description: "Organisations transitioning from agency retainers who require direct communication and focused, milestone-driven work.",
  benefit: "Single point of contact from audit to implementation",
  iconName: "ArrowRightLeft",
  to: "/ContactPage"
}];
function getIcon(name: string) {
  const iconProps = {
    className: "size-5 text-primary"
  };
  switch (name) {
    case "Building2":
      return <Building2 {...iconProps} />;
    case "Store":
      return <Store {...iconProps} />;
    case "BriefcaseBusiness":
      return <BriefcaseBusiness {...iconProps} />;
    case "ShoppingBag":
      return <ShoppingBag {...iconProps} />;
    case "MapPin":
      return <MapPin {...iconProps} />;
    case "Compass":
      return <Compass {...iconProps} />;
    case "Globe2":
      return <Globe2 {...iconProps} />;
    case "ArrowRightLeft":
      return <ArrowRightLeft {...iconProps} />;
    default:
      return <Building2 {...iconProps} />;
  }
}
export default function AudienceGrid() {
  return <section id="targetaudiencegrid" data-nav="light" className="relative bg-background py-20 md:py-28 overflow-hidden">
      {/* Subtle background decorative shapes in theme colors */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 right-1/4 size-96 rounded-full bg-secondary/40 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 size-96 rounded-full bg-muted/60 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-14 md:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 font-medium text-xs tracking-wide uppercase border border-border">
              Tailored Search Advisory
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif text-foreground tracking-tight">
            Who this SEO agency alternative is for
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: "easeOut"
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A tailored consultancy model offering clear milestones, transparent reporting, and direct collaboration focused on your business goals.
          </motion.p>
        </div>

        {/* Audience Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {audienceList.map((item, i) => <motion.div key={item.id} data-index={i} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          delay: i * 0.06,
          ease: "easeOut"
        }} className="h-full flex">
              <Card className="group relative flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/40 hover:shadow-lg transition-[border-color,box-shadow] duration-300 rounded-xl overflow-hidden p-0">
                {/* Accent top line on hover */}
                <div className="h-1 w-full bg-secondary group-hover:bg-primary transition-colors duration-300" />
                
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    {/* Top Row: Icon and Badge */}
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="flex items-center justify-center size-10 rounded-lg bg-secondary/80 border border-border/60 group-hover:bg-primary/10 transition-colors duration-300">
                        {getIcon(item.iconName)}
                      </div>
                      <span className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wider bg-background px-2.5 py-1 rounded-md border border-border">
                        {item.badge}
                      </span>
                    </div>

                    <CardHeader className="p-0 mb-3">
                      <CardTitle className="text-xl font-bold font-serif text-foreground group-hover:text-primary transition-colors duration-200">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardDescription className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {item.description}
                    </CardDescription>
                  </div>

                  {/* Benefit & Link */}
                  <div className="pt-4 border-t border-border/60 mt-auto">
                    <div className="flex items-start gap-2 mb-4">
                      <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                      <span className="text-xs font-medium text-foreground leading-snug">
                        {item.benefit}
                      </span>
                    </div>

                    <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-xs font-semibold text-primary hover:text-primary hover:bg-secondary/60">
                      <Link to={item.to}>
                        <span>Explore services</span>
                        <ArrowRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom CTA Strip */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3,
        ease: "easeOut"
      }} className="mt-14 sm:mt-18 rounded-2xl bg-secondary/40 border border-border/80 p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="max-w-xl">
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              Unsure which model fits your goals?
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              Book a direct initial consultation to review your current visibility, crawl data, and growth opportunities.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto shadow-sm">
              <Link to="/ContactPage">
                Book a Free Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/CaseStudiesPage">
                View Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}